import React, { useEffect } from "react";
import { ArrowLeft, ShieldCheck, Lock, Fingerprint, Ban, Eraser, ScrollText, Bot, Building2, Server, Database, Baby, RefreshCw } from "lucide-react";

const EDITOR = "Éditeur du site — [nom ou société à compléter]";
const CONTACT = "[adresse e-mail de contact à compléter]";

export default function Privacy() {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen py-24 px-5">
      <div className="max-w-3xl mx-auto">
        <a href="/" data-testid="privacy-back" className="btn-ghost inline-flex items-center gap-2 mb-8">
          <ArrowLeft size={14} />
          Retour à l'accueil
        </a>

        <div className="overline mb-4"><span className="kicker-line" />Politique de confidentialité & avertissement IA</div>
        <h1 className="font-serif text-4xl sm:text-5xl mb-6">
          Votre vie privée, <span className="italic" style={{ color: "var(--gold-glow)" }}>en clair</span>.
        </h1>
        <p className="text-lg mb-10" style={{ color: "var(--violet-soft)" }}>
          Le Chat Gréco-Celtique applique une politique de sobriété : nous collectons le minimum,
          nous chiffrons ce que nous gardons et nous l'effaçons vite.
        </p>

        <Section id="ia" icon={Bot} title="1. Des intelligences artificielles, pour le divertissement">
          <p>
            <b>Tous les personnages du site</b> (Barde, Druide, Vate, Gaïa, Zeus, Apollon, Oracle de Delphes,
            Phanès, archanges, éléments, etc.) sont des <b>intelligences artificielles</b>. Ce ne sont ni de vraies
            personnes, ni des voyants, ni des professionnels de santé.
          </p>
          <p>
            Leurs réponses sont générées automatiquement, <b>à but de divertissement et de culture uniquement</b>.
            Elles peuvent être inexactes, incomplètes ou inadaptées, et <b>ne remplacent jamais</b> un avis médical,
            psychologique, juridique ou financier. Aucune réponse ne constitue une prédiction.
          </p>
          <p>
            <b>Notre mission</b> : proposer un espace de philosophie et d'autoréflexion. Les personnages
            accompagnent sans diriger : ils peuvent <b>suggérer</b> des pistes avec bienveillance, mais <b>n'imposent rien</b> ; ils dédramatisent avec humour, posent des
            questions et éclairent avec des pensées philosophiques variées, pour aider chacun à trouver ses propres
            réponses et à gagner en autonomie, en détachement et en pleine conscience. Vos décisions vous appartiennent.
          </p>
          <p data-testid="privacy-style">
            <b>Style d'accompagnement</b> : les réponses passent par des images, métaphores, poésie, philosophie et
            une satire bienveillante qui dédramatise (jamais dirigée contre la personne). Chaque personnage pose des{" "}
            <b>questions ouvertes</b> qui invitent à l'introspection et à la reconnexion à soi et à son environnement.
            Toute action évoquée est un{" "}<b>simple exemple proposé, sans aucun délai ni obligation</b> (« exemple
            d'action concrète proposée, si le cœur t'en dit »). Un réconfort sincère peut être donné lorsque
            vous exprimez une peine ou un doute, sans flatterie ni complaisance.
          </p>
          <p>
            En cas de détresse, contactez le <b>3114</b> (prévention du suicide, France, gratuit, 24h/24) ou le <b>112</b>.
          </p>
        </Section>

        <Section id="editeur" icon={Building2} title="2. Responsable du traitement">
          <p data-testid="privacy-editor"><b>{EDITOR}</b></p>
          <p>Contact pour toute question ou demande liée à vos données : <span data-testid="privacy-contact" className="text-gold">{CONTACT}</span></p>
        </Section>

        <Section id="donnees" icon={Fingerprint} title="3. Données collectées et finalités">
          <p>Le site est <b>100 % gratuit, sans compte, sans inscription, sans paiement et sans infolettre</b>. Aucun courriel ni nom ne vous est demandé.</p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li><b>Messages de chat</b> — pour générer la réponse du personnage et garder le fil de la conversation.</li>
            <li><b>Adresse IP</b> — uniquement pour la sécurité (limitation des abus par minute).</li>
          </ul>
          <p className="mt-3">Base légale : exécution du service demandé et intérêt légitime (sécurité).</p>
        </Section>

        <Section id="ia-tiers" icon={Server} title="4. Prestataires et transmission à l'IA">
          <p>
            Pour produire une réponse, le texte de votre message est transmis à un <b>fournisseur de modèles d'IA</b>
            (modèles de langage de type Claude/Anthropic, via notre intermédiaire technique). Cette transmission sert
            uniquement à générer la réponse. Nous n'utilisons pas vos messages pour entraîner des modèles.
          </p>
          <p>Autre prestataire : hébergement du site et de la base de données.</p>
          <p>Certains prestataires peuvent être situés hors de l'Union européenne ; les transferts sont alors encadrés par les garanties prévues par le RGPD.</p>
        </Section>

        <Section id="chiffrement" icon={Lock} title="5. Chiffrement et sécurité">
          <ul className="list-disc pl-5 space-y-2">
            <li>Les messages de chat sont <b>chiffrés en base de données</b> (AES-256-GCM).</li>
            <li>Toutes les communications passent par HTTPS.</li>
            <li>Protection anti-abus : limitation de requêtes par IP, CAPTCHA et champ piège sur les formulaires.</li>
          </ul>
        </Section>

        <Section id="conservation" icon={Eraser} title="6. Durées de conservation">
          <ul className="list-disc pl-5 space-y-2">
            <li><b>Conversations</b> : effacées automatiquement après 1 heure d'inactivité, ou immédiatement avec le bouton « Effacer & partir » ou à la fermeture de l'onglet.</li>
            <li><b>Journaux techniques anti-abus</b> : 48 heures maximum.</li>
            <li><b>Questions aux oracles</b> : 24 heures.</li>
          </ul>
        </Section>

        <Section id="stockage" icon={Database} title="7. Cookies et stockage local">
          <p>
            Le site n'utilise <b>aucun cookie publicitaire ni traceur tiers</b>. Votre navigateur conserve localement
            (localStorage) quelques éléments techniques : identifiant de session de chat, langue, préférences de musique
            et votre acceptation de l'avertissement IA. Vous pouvez les effacer à tout moment depuis votre
            navigateur ou avec le bouton « Effacer & partir ».
          </p>
        </Section>

        <Section id="jamais" icon={Ban} title="8. Ce que nous ne faisons jamais">
          <ul className="list-disc pl-5 space-y-2">
            <li>Nous ne vendons aucune donnée.</li>
            <li>Nous ne faisons pas de publicité ciblée.</li>
            <li>Nous ne demandons jamais de données sensibles ; évitez d'en écrire dans les chats.</li>
          </ul>
        </Section>

        <Section id="mineurs" icon={Baby} title="9. Âge">
          <p>Le site est destiné aux personnes de <b>18 ans et plus</b>.</p>
        </Section>

        <Section id="droits" icon={ScrollText} title="10. Vos droits (RGPD)">
          <p>Vous pouvez demander l'accès, la rectification, l'effacement, la portabilité, la limitation ou vous opposer au traitement de vos données, et retirer votre consentement à tout moment.</p>
          <p>Écrivez à <span className="text-gold">{CONTACT}</span>. Vous pouvez aussi saisir la CNIL (cnil.fr).</p>
        </Section>

        <Section id="maj" icon={RefreshCw} title="11. Modifications et nouveautés">
          <p>Cette politique peut évoluer ; la date de mise à jour figure ci-dessous. Dernières nouveautés (juin 2026) :</p>
          <ul className="list-disc pl-5 space-y-1.5" data-testid="privacy-changelog">
            <li>Site <b>100 % gratuit et illimité</b> : suppression du paiement, de l'abonnement Premium, de l'infolettre et de toute demande de courriel ou de nom ; les adresses e-mail précédemment enregistrées ont été effacées.</li>
            <li>Suppression des portraits des personnages et du mode vocal avec micro (la musique d'ambiance et la lecture à voix haute des réponses restent).</li>
            <li>Nouveaux personnages IA : Phanès, l'Oracle de Delphes et les 7 consciences élémentaires (Eau, Air, Feu, Terre, Éther, Végétal, Minéral), avec leur page dédiée ; retrait des archanges Michel, Gabriel et Raphaël.</li>
            <li>Nouveau style d'accompagnement : suggestions indirectes et métaphoriques, questions ouvertes d'introspection, actions proposées sans aucun délai, réconfort seulement si besoin.</li>
            <li>Avertissement IA détaillé à la première visite, bandeau sur toutes les pages et rappel sous chaque chat.</li>
          </ul>
        </Section>

        <div className="mt-14 p-6 rounded-2xl" style={{ background: "rgba(212, 175, 55, 0.08)", border: "1px solid rgba(212, 175, 55, 0.3)" }}>
          <p className="text-sm flex items-center gap-2" style={{ color: "var(--violet-soft)" }}>
            <ShieldCheck size={14} className="text-gold" />
            Dernière mise à jour : juin 2026.
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} data-testid={`privacy-section-${id}`} className="mystic-card rounded-3xl p-7 mb-6 scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(212, 175, 55, 0.12)", border: "1px solid rgba(212, 175, 55, 0.3)" }}>
          <Icon size={16} className="text-gold" />
        </div>
        <h2 className="font-serif text-2xl">{title}</h2>
      </div>
      <div className="space-y-2 leading-relaxed" style={{ color: "var(--violet-soft)" }}>
        {children}
      </div>
    </section>
  );
}
